import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { useStats } from "@/context/StatsContext";
import { celebrate } from "@/lib/celebrate";
import BackButton from "@/components/BackButton";

export default function AddSong() {
  const [artist, setArtist] = useState("");
  const [title, setTitle] = useState("");
  const [lyrics, setLyrics] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();
  const statsCtx = useStats();

  const search = async () => {
    if (!artist.trim() && !title.trim()) return;
    setSearching(true);
    setResults([]);
    try {
      const { data } = await api.get("/lyrics/search", { params: { artist, title } });
      setResults(data);
      if (data.length === 0) toast.message("No matches — you can still paste lyrics below.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSearching(false); }
  };

  const pickResult = (r) => {
    setArtist(r.artistName);
    setTitle(r.trackName);
    setLyrics(r.plainLyrics || "");
    toast.success(`Loaded: ${r.trackName}`);
  };

  const save = async () => {
    if (!title.trim() || !artist.trim() || !lyrics.trim()) {
      toast.error("Title, artist, and lyrics are all needed.");
      return;
    }
    setSaving(true);
    try {
      const before = new Set(statsCtx?.stats?.achievements || []);
      const { data } = await api.post("/songs", { title, artist, lyrics });
      toast.success("Song saved.");
      // Refresh gamification stats and surface any freshly unlocked achievements
      try {
        const { data: newStats } = await api.get("/me/stats");
        statsCtx?.setStats?.(newStats);
        const newlyUnlocked = (newStats.achievements || []).filter((id) => !before.has(id));
        if (newlyUnlocked.length > 0) {
          celebrate("normal");
          const catalog = Object.fromEntries((newStats.achievements_catalog || []).map((a) => [a.id, a]));
          newlyUnlocked.forEach((id) => {
            const a = catalog[id];
            if (a) toast(`Unlocked: ${a.title}`, { description: a.desc });
          });
        }
      } catch (err) { console.warn("post-save stats refresh failed", err); }
      nav(`/songs/${data.id}`);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(formatApiError(e));
        nav("/billing");
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setSaving(false); }
  };

  return (
    <main data-testid="add-song-page" className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <BackButton to="/library" label="Back to library" testid="add-song-back-btn" />
      <p className="overline-label mt-6">Add a song</p>
      <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight">Find the lyrics, or paste your own.</h1>

      {/* Search block */}
      <section className="mt-10 border border-border rounded-2xl p-6 bg-background">
        <p className="overline-label mb-4">Search a public lyrics library</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="artist" className="overline-label">Artist</Label>
            <Input id="artist" value={artist} onChange={(e) => setArtist(e.target.value)}
                   placeholder="e.g. Adele" className="mt-2 h-11" data-testid="add-song-artist-input" />
          </div>
          <div>
            <Label htmlFor="title" className="overline-label">Song title</Label>
            <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="e.g. Someone Like You" className="mt-2 h-11" data-testid="add-song-title-input" />
          </div>
        </div>
        <Button onClick={search} disabled={searching} className="mt-4 rounded-full h-11" data-testid="add-song-search-btn">
          <Search size={16} className="mr-1.5" /> {searching ? "Searching…" : "Search lyrics"}
        </Button>

        {results.length > 0 && (
          <ul className="mt-6 divide-y divide-border border-y border-border" data-testid="add-song-results">
            {results.map((r) => (
              <li key={r.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{r.trackName}</p>
                  <p className="text-sm text-muted-foreground truncate">
                    {r.artistName}{r.albumName ? ` · ${r.albumName}` : ""}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="rounded-full"
                        onClick={() => pickResult(r)} data-testid={`add-song-pick-${r.id}`}>
                  Use lyrics
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Manual paste */}
      <section className="mt-8">
        <Label htmlFor="lyrics" className="overline-label">Lyrics</Label>
        <p className="text-sm text-muted-foreground mt-1 mb-3">
          Tip: use tags like <code className="bg-muted px-1.5 rounded">[Verse 1]</code> or
          <code className="bg-muted px-1.5 rounded ml-1">[Chorus]</code> to organize sections.
        </p>
        <Textarea
          id="lyrics"
          value={lyrics}
          onChange={(e) => setLyrics(e.target.value)}
          placeholder={`[Verse 1]\nI remember standing in the winter...\n\n[Chorus]\nAnd we sang, we sang, we sang...`}
          className="min-h-[280px] font-serif text-lg leading-relaxed"
          data-testid="add-song-lyrics-textarea"
        />
      </section>

      <div className="mt-8 flex justify-end gap-3">
        <Button variant="ghost" onClick={() => nav(-1)} className="rounded-full" data-testid="add-song-cancel-btn">
          Cancel
        </Button>
        <Button onClick={save} disabled={saving} className="rounded-full h-11 px-6" data-testid="add-song-save-btn">
          {saving ? "Saving…" : "Save song"}
        </Button>
      </div>
    </main>
  );
}
